import { logger } from '../log/log';
import { AuthError, NetworkError } from './types';

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

let authToken: string | null = null;
let _clearSession: (() => void) | null = null;

// Optional base URL prefix. The web app leaves this empty and relies on
// the Vite dev proxy to forward relative `/v1/...` requests to the BE
// host. RN-macOS has no origin/proxy, so the desktop bootstrap must
// call `setBaseUrl('https://openmls.ping.live')` (or whatever
// PING_API_BASE_URL it's pointed at) BEFORE any consumer fires a
// request — otherwise the bare `fetch('/v1/...')` call fails with
// "Network request failed". When unset, the original web-style
// relative-URL behaviour is preserved exactly.
let baseUrl = '';

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setClearSessionHandler(fn: () => void): void {
  _clearSession = fn;
}

/**
 * Configure a base URL prefix for every request the shared api fires.
 * No-op when the path is already absolute (callers can pass a full
 * URL through and the base is ignored). Pass `''` to revert to the
 * relative-URL behaviour the web relies on.
 */
export function setBaseUrl(url: string): void {
  baseUrl = url.replace(/\/+$/, '');
}

export function getBaseUrl(): string {
  return baseUrl;
}

async function sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/** True when the request opted out of the global reauth/logout-on-401. */
function readSkipReauth(init: RequestInit): boolean {
  const headers = init.headers;
  if (headers == null) return false;
  if (headers instanceof Headers) return headers.get('X-Ping-Skip-Reauth') === '1';
  if (Array.isArray(headers)) {
    return headers.some(([k, v]) => k.toLowerCase() === 'x-ping-skip-reauth' && v === '1');
  }
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() === 'x-ping-skip-reauth' && v === '1') return true;
  }
  return false;
}

async function fetchWithRetry<T>(
  url: string,
  init: RequestInit,
  maxRetries: number = 3,
): Promise<T> {
  const retryDelays = [250, 500, 1000];
  let lastError: Error | null = null;
  let lastStatus: number | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await globalThis.fetch(url, init);
      lastStatus = response.status;

      if (response.status === 401) {
        // Requests flagged `X-Ping-Skip-Reauth` (e.g. the device-linking
        // ticket build/fetch) are foreground actions that must FAIL
        // INLINE — never tear down the session. Surface an AuthError to
        // the caller but skip the global `_clearSession()` logout.
        const skipReauth = readSkipReauth(init);
        logger.warn('Authentication failed', { status: 401, path: url, skipReauth });
        if (_clearSession && !skipReauth) {
          _clearSession();
        }
        throw new AuthError('Authentication required');
      }

      if (!response.ok) {
        if (response.status >= 500 && response.status < 600 && attempt < maxRetries) {
          logger.debug('Server error, retrying', {
            status: response.status,
            attempt: attempt + 1,
            maxRetries,
          });
          const delay = retryDelays[attempt];
          if (delay !== undefined) {
            await sleep(delay);
          }
          continue;
        }
        
        const httpError = new Error(`HTTP ${response.status}`);
        if (response.status >= 500 && response.status < 600) {
          throw new NetworkError(httpError.message, attempt, response.status);
        }
        throw httpError;
      }

      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/vnd.ping.wire.v1+cbor')) {
        const arrayBuffer = await response.arrayBuffer();
        return arrayBuffer as unknown as T;
      }

      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return (await response.text()) as unknown as T;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }

      if (error instanceof NetworkError) {
        throw error;
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }

      if (error instanceof Error && error.message.startsWith('HTTP ')) {
        throw error;
      }

      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < maxRetries) {
        logger.debug('Request failed, retrying', {
          attempt: attempt + 1,
          maxRetries,
          error: lastError.message,
        });
        const delay = retryDelays[attempt];
        if (delay !== undefined) {
          await sleep(delay);
        }
      }
    }
  }

  logger.error('Request failed after all retries', undefined, {
    maxRetries,
    lastStatus,
  });
  throw new NetworkError(
    lastError?.message || 'Network request failed',
    maxRetries,
    lastStatus,
  );
}

function buildHeaders(additionalHeaders?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/vnd.ping.wire.v1+cbor',
    ...additionalHeaders,
  };

  // Attach the default session bearer ONLY when the caller hasn't already
  // supplied its own Authorization. Some flows pass a different, scoped
  // credential (e.g. the recovery-restore GET sends a short-lived recovery
  // JWT, not the device session token) — clobbering it here would send the
  // wrong token and 401/403 the request.
  const hasExplicitAuth = Object.keys(headers).some(
    (key) => key.toLowerCase() === 'authorization',
  );
  if (authToken && !hasExplicitAuth) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: RequestOptions,
): Promise<T> {
  // Prefix with the configured base URL unless the caller already
  // passed an absolute URL. Both `http(s)://` and `ws(s)://` count as
  // absolute. Empty baseUrl preserves the web's relative-path behaviour
  // (Vite proxy forwards them).
  const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(path);
  const url = isAbsolute || baseUrl === '' ? path : `${baseUrl}${path}`;
  const headers = buildHeaders(opts?.headers);

  const init: RequestInit = {
    method,
    headers,
    signal: opts?.signal ?? null,
  };

  if (body !== undefined) {
    if (body instanceof ArrayBuffer || body instanceof Uint8Array) {
      init.body = body as BodyInit;
      headers['Content-Type'] = 'application/vnd.ping.wire.v1+cbor';
    } else {
      init.body = JSON.stringify(body);
      headers['Content-Type'] = 'application/json';
    }
  }

  logger.debug('HTTP request', {
    method,
    path,
  });

  return fetchWithRetry<T>(url, init);
}

export const api = {
  get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, opts);
  },

  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body, opts);
  },

  put<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body, opts);
  },

  delete<T>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, undefined, opts);
  },
};
